<script setup>

import { computed } from 'vue'

const props = defineProps({
  agentConfig: {
    type: Object,
    required: true
  },
  isProcessing: {
    type: Boolean,
    default: false
  },
  actionButtonText: {
    type: String,
    default: 'Train'
  },
  statusText: {
    type: String,
    default: ''
  },
  onUpdate: {
    type: Function,
    required: true
  },
  onWeblinkShare: {
    type: Function,
    required: true
  }
});

const agentStatus = computed(() => props.agentConfig.status || 'draft')

const statusClass = computed(() => `status-${agentStatus.value}`)

const isStatusBlocking = computed(() => {
  return agentStatus.value === 'pending' || agentStatus.value === 'training'
})

const isActionDisabled = computed(() => {
  return props.isProcessing || !props.agentConfig.name?.trim() || isStatusBlocking.value
})
</script>

<template>
  <div id="app" class="app-container">
    <div class="header">
      <div class="left-action">
        <router-link to="/agents">
          <div class="go-back">
            <img src="@/assets/images/arrowLeft.svg" />
            <span>{{$t('go_back_button')}}</span>
          </div>
        </router-link>
      </div>

      <div class="center-status">
        <div class="agent-status-badge" :class="statusClass">
          <div class="status-dot"></div>
          <span class="status-text">{{statusText}}</span>
        </div>
      </div>

      <div class="right-actions">
        <div class="action-button-group">

          <div class="action-button-item" @click="onWeblinkShare">
            <img src="@/assets/images/link.svg" alt="Settings" />
          </div>
        </div>

        <button
            @click="onUpdate()"
            :disabled="isActionDisabled"
            class="action-primary-button"
            :class="{ 'disabled': isActionDisabled }"
        >
          <span>{{ actionButtonText }}</span>
        </button>
      </div>
    </div>

    <div class="agent-container">
      <slot />
    </div>
  </div>
</template>


<style scoped>
.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  background-color: #FCFCFD;
  overflow-y: auto;
}

.header {
  width: 100%;
  background-color: #FFFFFF;
  border-bottom: 1px solid #EAECF0;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 20px;
  padding-right: 20px;
  z-index: 9999;
  position: relative;
}

.center-status {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
}

.agent-container {
  margin: 0 auto;
  width: 100%;
}

.go-back {
  display: flex;
  flex-direction: row;
  gap: 10px;
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
}

.right-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.right-actions span {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
}

.left-action a {
  color: #040C2D;
}

.left-action a:active {
  color: #040C2D;
}

.left-action a:visited {
  color: #040C2D;
}

.left-action a:hover {
  color: #040C2D;
}

.action-link {
  cursor: pointer;
  color: #1C3CD5;
}

.log-icon{
  display: flex;
  cursor: pointer;
  align-self: center;
  width: 16px;
  height: 16px;
}

/* Status Badge Styles */
.agent-status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
  white-space: nowrap;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-text {
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
}

/* Status Colors - Same as AgentsCard */
.status-draft {
  background-color: #F2F4F7;
  color: #667085;
}

.status-draft .status-dot {
  background-color: #667085;
}

.status-pending {
  background-color: #FFFAEB;
  color: #B54708;
}

.status-pending .status-dot {
  background-color: #F79009;
}

.status-training {
  background-color: #EFF8FF;
  color: #175CD3;
}

.status-training .status-dot {
  background-color: #2E90FA;
}

.status-published {
  background-color: #ECFDF3;
  color: #027A48;
}

.status-published .status-dot {
  background-color: #12B76A;
}

.status-failed {
  background-color: #FEF3F2;
  color: #B42318;
}

.status-failed .status-dot {
  background-color: #F04438;
}

.status-deleted {
  background-color: #F9FAFB;
  color: #475467;
}

.status-deleted .status-dot {
  background-color: #98A2B3;
}


/* Button Disabled State */
.action-primary-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    min-height: auto;
    padding: 16px 20px;
    gap: 12px;
  }

  .center-status {
    position: static;
    transform: none;
    order: 2;
  }

  .left-action {
    order: 1;
    align-self: flex-start;
  }

  .right-actions {
    order: 3;
    align-self: flex-end;
  }
}
</style>
